import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, Users, Pencil, Layout, Calendar, Eye, Trash2 } from 'lucide-react';
import { deleteEvent, getEventById } from '../api/events';

const formatDateTime = (date, time) => {
  if (!date) return "Date to be announced";
  // Assuming date is in YYYY-MM-DD format and time is HH:MM
  const combinedString = `${String(date).split('T')[0]}T${time || "00:00"}`;
  const dateTimeObject = new Date(combinedString);

  if (Number.isNaN(dateTimeObject.getTime())) {
    return String(date);
  }

  return dateTimeObject.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    // hours: 'numeric',
    // minutes: 'numeric',
  });
};

const toTitleCase = (value) =>
  String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const isInternalDraftName = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  return /^royalnew\d*$/.test(normalized) || /^template\s*\d*$/.test(normalized);
};

const getFriendlyEventTitle = (event) => {
  if (event?.title && !isInternalDraftName(event.title)) {
    return event.title;
  }

  const eventType = toTitleCase(event?.event_type_name || event?.template_event_type);
  if (eventType) {
    return `${eventType} Celebration`;
  }

  return "Celebration Event";
};

const normalizeGuestMetrics = (metrics = {}) => ({
  total_guests: Number(metrics.total_guests || 0),
  sent: Number(metrics.sent || 0),
  rsvp_yes: Number(metrics.rsvp_yes || 0),
  rsvp_no: Number(metrics.rsvp_no || 0),
  pending: Number(metrics.pending || 0),
});

// Mock Data structure reflecting the Event model and aggregated guest metrics
const MOCK_ORGANIZER_EVENTS = [
  {
    id: "e123-abc-001", 
    title: "The Grand Wedding Reception", 
    status: "active", 
    design_finalized: true, 
    event_date: "2025-12-15", 
    event_time: "18:00",
    image_url: "https://placehold.co/600x400/FF5C8A/FFFFFF?text=Wedding+Cover",
    cover_video: null,
    guest_metrics: { sent: 150, rsvp_yes: 110, rsvp_no: 5 },
  },
  {
    id: "e123-abc-002", 
    title: "Q4 Team Launch Party", 
    status: "draft", 
    design_finalized: false, 
    event_date: "2025-11-28", 
    event_time: "14:30",
    image_url: "https://placehold.co/600x400/2A7090/FFFFFF?text=Launch+Event",
    cover_video: null,
    guest_metrics: { sent: 50, rsvp_yes: 0, rsvp_no: 0 },
  },
  {
    id: "e123-abc-003", 
    title: "Summer BBQ 2024", 
    status: "past", 
    design_finalized: true, 
    event_date: "2024-07-20", 
    event_time: "12:00",
    image_url: "https://placehold.co/600x400/FFB700/000000?text=Past+BBQ",
    cover_video: null,
    guest_metrics: { sent: 80, rsvp_yes: 75, rsvp_no: 2 },
  },
];

// Helper to determine status tag class
const getStatusClass = (status) => {
  if (status === 'active') return 'active';
  if (status === 'draft') return 'draft';
  if (status === 'past') return 'past';
  return '';
};

const EventCardOrganizer = ({ event, onDeleted }) => {
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleEditDetails = () => {
    navigate('/event-manager', { state: { eventId: event.id } });
  };

  const handleManageGuests = () => {
    navigate('/guest-manager', { state: { eventId: event.id } });
  };

  const handleEditDesign = async () => {
    let templateId = event.template_id;

    if (!templateId) {
      try {
        const fullEvent = await getEventById(event.id);
        templateId = fullEvent?.template_id;
      } catch (error) {
        console.error("Failed to resolve event template:", error);
      }
    }

    if (!templateId) {
      window.alert("This event isn't linked to a template yet, so we can't reopen it in the editor.");
      return;
    }

    navigate(`/editor/${templateId}`, {
      state: {
        mode: 'event',
        eventId: event.id,
      },
    });
  };

  const handlePreviewExperience = () => {
    navigate(`/events/${event.id}/experience`);
  };

  const handleDeleteEvent = async () => {
    const confirmed = window.confirm(
      `Delete "${getFriendlyEventTitle(event)}"? This will remove the event, its guest links, and related dashboard data.`
    );

    if (!confirmed) {
      return;
    }

    try {
      setIsDeleting(true);
      await deleteEvent(event.id);
      onDeleted?.();
    } catch (error) {
      window.alert(error?.message || "We couldn't delete this event.");
    } finally {
      setIsDeleting(false);
    }
  };
  
  const metrics = normalizeGuestMetrics(event.guest_metrics);
  const totalResponded = metrics.rsvp_yes + metrics.rsvp_no;
  const displayDate = formatDateTime(event.event_date, event.event_time);
  const displayTitle = getFriendlyEventTitle(event);

  return (
    <div className={`event-card ${event.status === 'past' ? 'past-card' : ''}`}>
      <div className="card-image-wrapper">
        {event.cover_video ? (
          <video
            src={event.cover_video}
            className="card-cover-image"
            autoPlay
            muted
            loop
            playsInline
          />
        ) : (
          <img 
            src={event.image_url} 
            alt={`${displayTitle} cover`} 
            className="card-cover-image" 
            onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/600x400/999999/FFFFFF?text=No+Image"; }}
          />
        )}
        <span className={`status-tag ${getStatusClass(event.status)}`}>
          {event.status.toUpperCase()}
        </span>
      </div>

      <div className="card-content">
        <div className="card-header">
          <h3>{displayTitle}</h3>
          <div className="card-icon-actions">
            <button className="icon-button primary" onClick={handleEditDetails} title="Edit Details">
              <Settings size={20} />
            </button>
            <button className="icon-button primary" onClick={handleManageGuests} title="Manage Guests">
              <Users size={20} />
            </button>
            <button className="icon-button secondary" onClick={handlePreviewExperience} title="Preview Guest Experience">
              <Eye size={20} />
            </button>
            <button 
              className={`icon-button ${!event.design_finalized ? 'draft-icon' : 'secondary'}`} 
              onClick={handleEditDesign} 
              title={!event.design_finalized ? 'Finalize Design' : 'Edit Design'}
            >
              {!event.design_finalized ? <Layout size={20} /> : <Pencil size={20} />}
            </button>
            <button
              className="icon-button secondary"
              onClick={handleDeleteEvent}
              title="Delete Event"
              disabled={isDeleting}
            >
              <Trash2 size={20} />
            </button>
          </div>
        </div>

        <p className="card-date-time"><Calendar size={14} style={{ marginRight: '5px' }} />{displayDate}</p>

        <div className="metrics-row">
          <div className="metric-item">
            <strong>{metrics.sent}</strong>
            <span>Sent</span>
          </div>
          <div className="metric-item">
            <strong>{metrics.rsvp_yes}</strong>
            <span>Attending</span>
          </div>
          <div className="metric-item">
            <strong>{totalResponded}</strong>
            <span>Responded</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export const EventsOrganizer = ({events, onRefresh}) => {
  const allEvents = Array.isArray(events) ? events : MOCK_ORGANIZER_EVENTS;
  const [showPast, setShowPast] = useState(false);
  const activeEvents = allEvents.filter(e => e.status !== 'past');
  const pastEvents = allEvents.filter(e => e.status === 'past');

  return (
    <div>
      <div className="event-list-view">
        {activeEvents.map(event => (
          <EventCardOrganizer key={event.id} event={event} onDeleted={onRefresh} />
        ))}
      </div>

      {/* Past Events Toggle */}
      {pastEvents.length > 0 && (
        <button 
          className="past-toggle-button"
          onClick={() => setShowPast(!showPast)}
        >
          {showPast ? 'Hide Past Events' : `Show ${pastEvents.length} Past Events`}
        </button>
      )}

      {/* Past Events List */}
      {showPast && pastEvents.length > 0 && (
        <div className="past-events-list">
          <h4>Past Events History</h4>
          <div className="event-list-view">
            {pastEvents.map(event => (
              <EventCardOrganizer key={event.id} event={event} onDeleted={onRefresh} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
